/**
 * Crew chat binding (client) — multi-mate launch + per-chat roster
 *
 * Carries Crew Chat from Your crew into /chat:
 * - stageCrewChatLaunch: one-time nonce in sessionStorage → /chat?crewLaunch=
 * - bindCrewChatToNextChat / resolveCrewChatForChatRequest: first request
 *   after launch binds chatId → roster + computer mode + active member
 *
 * Security: only same-origin code writes sessionStorage (no URL-driven tools).
 */

import {
  buildDefaultCrewChatRoster,
  normalizeCrewChatComputerMode,
  normalizeCrewChatRoster,
  type CrewChatComputerMode,
  type CrewChatRoster
} from '@/lib/crew/crew-chat'
import { normalizeCrewMemberId, type CrewMemberId } from '@/lib/crew/member-access'

const LAUNCH_PREFIX = 'losi:crew-chat-launch:'
const BINDINGS_KEY = 'losi:crew-chat-bindings'
const ROSTER_BINDINGS_KEY = 'losi:crew-chat-roster-bindings'
const PENDING_KEY = 'losi:crew-chat-pending-member'
const PENDING_ROSTER_KEY = 'losi:crew-chat-pending-roster'
const LAUNCH_TTL_MS = 2 * 60 * 1000
const MAX_BINDINGS = 50

export const CREW_CHAT_LAUNCH_PARAM = 'crewLaunch' as const

export type CrewChatLaunch = {
  /** Active / lead speaker for computer rules (leader by default). */
  crewMemberId: CrewMemberId | null
  prompt: string
  roster: CrewChatRoster
  computerMode: CrewChatComputerMode
}

// --------------

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

function createNonce(): string {
  const bytes = new Uint8Array(12)
  globalThis.crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function readBindings(): Record<string, CrewMemberId> {
  const raw = storage()?.getItem(BINDINGS_KEY)
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const result: Record<string, CrewMemberId> = {}
    for (const [chatId, member] of Object.entries(parsed)) {
      const normalized = normalizeCrewMemberId(member)
      if (normalized) result[chatId] = normalized
    }
    return result
  } catch {
    return {}
  }
}

function writeBindings(bindings: Record<string, CrewMemberId>) {
  const entries = Object.entries(bindings).slice(-MAX_BINDINGS)
  storage()?.setItem(BINDINGS_KEY, JSON.stringify(Object.fromEntries(entries)))
}

function readRosterBindings(): Record<string, CrewChatRoster> {
  const raw = storage()?.getItem(ROSTER_BINDINGS_KEY)
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const result: Record<string, CrewChatRoster> = {}
    for (const [chatId, roster] of Object.entries(parsed)) {
      const normalized = normalizeCrewChatRoster(roster)
      if (normalized) result[chatId] = normalized
    }
    return result
  } catch {
    return {}
  }
}

function writeRosterBindings(bindings: Record<string, CrewChatRoster>) {
  const entries = Object.entries(bindings).slice(-MAX_BINDINGS)
  storage()?.setItem(
    ROSTER_BINDINGS_KEY,
    JSON.stringify(Object.fromEntries(entries))
  )
}

function readPendingMember(store: Storage): CrewMemberId | null {
  const raw = store.getItem(PENDING_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { at?: unknown; member?: unknown }
    const at = typeof parsed.at === 'number' ? parsed.at : 0
    if (Date.now() - at > LAUNCH_TTL_MS) return null
    return normalizeCrewMemberId(parsed.member)
  } catch {
    return null
  }
}

function readPendingRoster(store: Storage): CrewChatRoster | null {
  const raw = store.getItem(PENDING_ROSTER_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { at?: unknown; roster?: unknown }
    const at = typeof parsed.at === 'number' ? parsed.at : 0
    if (Date.now() - at > LAUNCH_TTL_MS) return null
    return normalizeCrewChatRoster(parsed.roster)
  } catch {
    return null
  }
}

// --------------

/** Stage a Crew → chat launch; returns the `/chat` href to navigate to. */
export function stageCrewChatLaunch(input: {
  crewMemberId?: string | null
  prompt: string
  roster?: CrewChatRoster | null
  computerMode?: CrewChatComputerMode | null
}): string | null {
  const prompt = input.prompt.trim()
  const store = storage()
  if (!prompt || !store) return null

  const computerMode = normalizeCrewChatComputerMode(
    input.computerMode ?? input.roster?.computerMode
  )
  const roster =
    normalizeCrewChatRoster(input.roster) ??
    buildDefaultCrewChatRoster(computerMode)

  const active =
    normalizeCrewMemberId(input.crewMemberId) ?? roster.leaderMateId

  const nonce = createNonce()
  store.setItem(
    `${LAUNCH_PREFIX}${nonce}`,
    JSON.stringify({
      crewMemberId: active,
      prompt,
      roster,
      computerMode: roster.computerMode,
      stagedAt: Date.now()
    })
  )
  return `/chat?${CREW_CHAT_LAUNCH_PARAM}=${nonce}`
}

/** One-time read of a staged launch (deleted on read; expires after 2 min). */
export function consumeCrewChatLaunch(
  nonce: string | null | undefined
): CrewChatLaunch | null {
  const store = storage()
  if (!store || !nonce || !/^[a-f0-9]{24}$/.test(nonce)) return null

  const key = `${LAUNCH_PREFIX}${nonce}`
  const raw = store.getItem(key)
  store.removeItem(key)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as {
      crewMemberId?: unknown
      prompt?: unknown
      roster?: unknown
      computerMode?: unknown
      stagedAt?: unknown
    }
    const stagedAt = typeof parsed.stagedAt === 'number' ? parsed.stagedAt : 0
    if (Date.now() - stagedAt > LAUNCH_TTL_MS) return null
    const prompt = typeof parsed.prompt === 'string' ? parsed.prompt.trim() : ''
    if (!prompt) return null

    const computerMode = normalizeCrewChatComputerMode(parsed.computerMode)
    const roster =
      normalizeCrewChatRoster(parsed.roster) ??
      buildDefaultCrewChatRoster(computerMode)

    return {
      crewMemberId:
        normalizeCrewMemberId(parsed.crewMemberId) ?? roster.leaderMateId,
      prompt,
      roster,
      computerMode: roster.computerMode
    }
  } catch {
    return null
  }
}

// --------------

/** The next chat request (a brand-new chat) belongs to this member + roster. */
export function bindCrewMemberToNextChat(crewMemberId: string | null | undefined) {
  const member = normalizeCrewMemberId(crewMemberId)
  const store = storage()
  if (!store) return
  if (member) {
    store.setItem(PENDING_KEY, JSON.stringify({ member, at: Date.now() }))
  } else {
    store.removeItem(PENDING_KEY)
  }
}

export function bindCrewChatToNextChat(input: {
  crewMemberId?: string | null
  roster?: CrewChatRoster | null
}) {
  const store = storage()
  if (!store) return

  const roster = normalizeCrewChatRoster(input.roster)
  const member =
    normalizeCrewMemberId(input.crewMemberId) ??
    roster?.leaderMateId ??
    null

  bindCrewMemberToNextChat(member)

  if (roster) {
    store.setItem(
      PENDING_ROSTER_KEY,
      JSON.stringify({ roster, at: Date.now() })
    )
  } else {
    store.removeItem(PENDING_ROSTER_KEY)
  }
}

/**
 * Member for an outgoing chat request. Binds a pending launch to the first
 * chatId it sees; existing chats keep their binding.
 *
 * When chatId is missing (brand-new Crew send before the row exists), still
 * return the pending member so tools-stream gets crewMemberId on the first
 * request — otherwise show_plan stays `draft` and Approvals stay empty.
 */
export function resolveCrewMemberForChatRequest(
  chatId: string | null | undefined
): CrewMemberId | null {
  const store = storage()
  if (!store) return null

  const pending = readPendingMember(store)
  if (typeof chatId !== 'string' || !chatId.trim()) {
    return pending
  }

  const bindings = readBindings()
  const bound = bindings[chatId]
  if (bound) return bound

  if (!pending) return null

  store.removeItem(PENDING_KEY)
  writeBindings({ ...bindings, [chatId]: pending })
  return pending
}

export function resolveCrewChatRosterForChatRequest(
  chatId: string | null | undefined
): CrewChatRoster | null {
  const store = storage()
  if (!store) return null

  const pending = readPendingRoster(store)
  if (typeof chatId !== 'string' || !chatId.trim()) {
    return pending
  }

  const bindings = readRosterBindings()
  const bound = bindings[chatId]
  if (bound) return bound

  if (!pending) return null

  store.removeItem(PENDING_ROSTER_KEY)
  writeRosterBindings({ ...bindings, [chatId]: pending })
  return pending
}

/**
 * Persist member/roster onto a chatId after the first send knows the id.
 * Safe to call every request; no-ops when inputs are empty.
 */
export function ensureCrewBindingForChat(input: {
  chatId: string | null | undefined
  crewMemberId?: string | null
  roster?: CrewChatRoster | null
}) {
  const store = storage()
  if (!store || typeof input.chatId !== 'string' || !input.chatId.trim()) return

  const chatId = input.chatId.trim()
  const member = normalizeCrewMemberId(input.crewMemberId)
  if (member) {
    const bindings = readBindings()
    if (bindings[chatId] !== member) {
      writeBindings({ ...bindings, [chatId]: member })
    }
    store.removeItem(PENDING_KEY)
  }

  const roster = normalizeCrewChatRoster(input.roster)
  if (roster) {
    const rosterBindings = readRosterBindings()
    writeRosterBindings({ ...rosterBindings, [chatId]: roster })
    store.removeItem(PENDING_ROSTER_KEY)
  }
}

/** Read-only lookup (UI chrome). */
export function getCrewMemberForChat(
  chatId: string | null | undefined
): CrewMemberId | null {
  if (typeof chatId !== 'string' || !chatId.trim()) return null
  return readBindings()[chatId] ?? null
}

export function getCrewChatRosterForChat(
  chatId: string | null | undefined
): CrewChatRoster | null {
  if (typeof chatId !== 'string' || !chatId.trim()) return null
  return readRosterBindings()[chatId] ?? null
}
